import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  FileText, 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Download,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface SEOReport {
  id: string;
  report_type: string;
  report_period_start: string;
  report_period_end: string;
  overall_score: number;
  pages_analyzed: number;
  total_issues: number;
  critical_issues: number;
  warnings: number;
  suggestions: number;
  performance_metrics: {
    avg_score: number;
    score_trend: 'improving' | 'declining' | 'stable';
    top_performing_pages: Array<{ url: string; score: number }>;
    most_issues_pages: Array<{ url: string; issue_count: number }>;
  };
  top_issues: Array<{ issue: string; count: number }>;
  improvements: string[];
  generated_at: string;
}

interface SEOAlert {
  type: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  details: any;
}

const SEOReportsPanel: React.FC = () => {
  const [reports, setReports] = useState<SEOReport[]>([]);
  const [alerts, setAlerts] = useState<SEOAlert[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCheckingAlerts, setIsCheckingAlerts] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('seo_reports')
        .select('*')
        .order('generated_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      // Type-safe casting with proper JSON parsing
      const typedReports = (data || []).map(report => ({
        ...report,
        performance_metrics: typeof report.performance_metrics === 'string' 
          ? JSON.parse(report.performance_metrics) 
          : report.performance_metrics || {},
        top_issues: typeof report.top_issues === 'string' 
          ? JSON.parse(report.top_issues) 
          : report.top_issues || [],
        improvements: typeof report.improvements === 'string' 
          ? JSON.parse(report.improvements) 
          : report.improvements || []
      })) as SEOReport[];
      
      setReports(typedReports);
    } catch (error: any) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    }
  }, []);

  const generateReport = useCallback(async () => {
    try {
      setIsGenerating(true);
      const response = await supabase.functions.invoke('seo-monitoring', {
        body: { action: 'generate_report' }
      });

      if (response.error) throw response.error;
      
      toast.success('Report generated successfully');
      fetchReports();
    } catch (error: any) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  }, [fetchReports]);

  const checkAlerts = useCallback(async () => {
    try {
      setIsCheckingAlerts(true);
      const response = await supabase.functions.invoke('seo-monitoring', {
        body: { action: 'check_alerts' }
      });

      if (response.error) throw response.error;
      setAlerts(response.data?.alerts || []);
    } catch (error: any) {
      console.error('Failed to check alerts:', error);
      toast.error('Failed to check alerts');
    } finally {
      setIsCheckingAlerts(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
    checkAlerts();
  }, [fetchReports, checkAlerts]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'declining': return <TrendingDown className="h-4 w-4 text-red-600" />;
      default: return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'warning': return 'outline';
      default: return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      {/* Alerts Section */}
      {alerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Active SEO Alerts
            </CardTitle>
            <CardDescription>Issues requiring immediate attention</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {alerts.map((alert, idx) => (
              <div key={idx} className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                      <span className="font-medium">{alert.type.replace('_', ' ')}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-700">{alert.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Reports Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            SEO Reports & Analytics
          </CardTitle>
          <CardDescription>
            Generate and view comprehensive SEO performance reports
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Button onClick={generateReport} disabled={isGenerating} className="gap-2">
              {isGenerating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {isGenerating ? 'Generating...' : 'Generate Weekly Report'}
            </Button>
            <Button variant="outline" onClick={checkAlerts} disabled={isCheckingAlerts} className="gap-2">
              {isCheckingAlerts ? <RefreshCw className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
              Check Alerts
            </Button>
          </div>

          {/* Recent Reports */}
          {reports.length > 0 && (
            <div className="space-y-4">
              <h4 className="font-medium">Recent Reports</h4>
              {reports.map((report) => (
                <div key={report.id} className="rounded-lg border p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{report.report_type}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {new Date(report.generated_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div>
                          <div className="text-sm text-muted-foreground">Overall Score</div>
                          <div className={`text-xl font-semibold ${getScoreColor(report.overall_score)}`}>
                            {report.overall_score}
                          </div>
                          <Progress value={report.overall_score} className="mt-1" />
                        </div>
                        
                        <div>
                          <div className="text-sm text-muted-foreground">Pages</div>
                          <div className="text-xl font-semibold">{report.pages_analyzed}</div>
                        </div>
                        
                        <div>
                          <div className="text-sm text-muted-foreground">Critical Issues</div>
                          <div className="text-xl font-semibold text-red-600">{report.critical_issues}</div>
                        </div>
                        
                        <div>
                          <div className="text-sm text-muted-foreground">Warnings</div>
                          <div className="text-xl font-semibold text-yellow-600">{report.warnings}</div>
                        </div>
                        
                        <div>
                          <div className="text-sm text-muted-foreground">Trend</div>
                          <div className="flex items-center gap-1">
                            {getTrendIcon(report.performance_metrics.score_trend)}
                            <span className="text-sm capitalize">{report.performance_metrics.score_trend}</span>
                          </div>
                        </div>
                      </div>

                      {/* Top Issues */}
                      {report.top_issues.length > 0 && (
                        <div className="mt-4">
                          <div className="text-sm font-medium mb-2">Top Issues</div>
                          <div className="flex flex-wrap gap-2">
                            {report.top_issues.slice(0, 3).map((issue, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {issue.issue} ({issue.count})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Improvements */}
                      {report.improvements.length > 0 && (
                        <div className="mt-4">
                          <div className="text-sm font-medium mb-2">Key Improvements</div>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {report.improvements.slice(0, 2).map((improvement, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <CheckCircle className="h-3 w-3 mt-0.5 text-green-600 flex-shrink-0" />
                                {improvement}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SEOReportsPanel;